import { includes, isEqual, isString, mapValues, split } from 'lodash-es';
import queryString from 'query-string';
import { useMemo } from 'react';

export interface Router {
  query: Query;
  route: string;
  push: (url: string, as?: string, routerOptions?: { shallow: boolean }) => void;
  replace: (url: string, as?: string, routerOptions?: { shallow: boolean }) => void;
}

export type Query = Record<string, string | string[] | undefined>;

export type Dispatch<T = Query> = ((query: T) => T) | T;

function useUrlState<T extends Query>({
  router,
  isReplace,
  option,
  routerOptions,
}: {
  router: Router;
  isReplace?: boolean;
  option?: queryString.StringifyOptions;
  routerOptions?: { shallow: boolean };
}) {
  const { query, route, push, replace } = router;

  const typedQuery = query as Partial<T>;

  const setValue = (dispatch: Dispatch<Partial<T>>, path?: string) => {
    const newValue = dispatch instanceof Function ? dispatch(typedQuery) : dispatch;

    if (isEqual(typedQuery, newValue)) {
      return;
    }

    (isReplace ? replace : push)(
      `${path || route}?${queryString.stringify(newValue, {
        arrayFormat: 'comma',
        skipEmptyString: true,
        ...option,
      })}`,
      undefined,
      routerOptions,
    );
  };

  const parsedValue = useMemo(
    () =>
      mapValues(typedQuery, (value) =>
        isString(value) && includes(value, ',') ? split(value, ',') : value,
      ),
    [typedQuery],
  ) as Partial<T>;

  return {
    value: parsedValue,
    setValue,
  };
}

export default useUrlState;
